import { useEffect, useRef, useState } from 'react';
import type { CrmContact } from '../../api/types';
import type { ChannelKey } from '../../lib/commsMeta';
import { useComms } from './context';
import { parseReachIntent, pickContact, pickConversation, type ReachIntent } from './reachIntent';

/**
 * What the compose window should open with when there is no thread yet — the
 * person if we found them, otherwise the bare address they were named by.
 */
export interface ReachSeed {
  intent: ReachIntent;
  contact: CrmContact | null;
}

/**
 * Resolve `?to=<phone|email>` into something on screen.
 *
 * The dispatch dashboard's call, mail and SMS icons name a customer by address
 * because that is all a reservation row knows about them. This turns that into
 * the conversation they already have, or a compose window seeded with them.
 *
 * The parameter is a REQUEST, not a location: it is cleared as soon as it has
 * been acted on. Leaving it in the URL would re-open the thread every time the
 * agent switched tabs, and would fight the conversation they navigated to next.
 *
 * Failure resolves to a seeded compose window rather than an error. The agent
 * pressed a button meaning "reach this person" — a contact lookup that could
 * not answer is a reason to let them write the message, not to stop them.
 */
export function useReach(
  to: string | null,
  tab: string | null,
  handlers: {
    /** Show this conversation AND drop `to` — one URL write, not two. */
    openConversation: (id: number, channelKey: ChannelKey) => void;
    /** Drop `to`, having nothing to open. */
    clear: () => void;
  },
): { seed: ReachSeed | null; clearSeed: () => void } {
  const { source, inboxes, inboxesLoading } = useComms();
  const [seed, setSeed] = useState<ReachSeed | null>(null);

  // Handlers are rebuilt every render; holding them in a ref keeps them out of
  // the dependency list, so the effect runs when the ADDRESS changes and not
  // whenever the module re-renders around it.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  // Channel keys are read off inboxes, so a resolution that ran before they
  // landed would compose at a customer who has an open thread. `inboxes` itself
  // is deliberately not a dependency: it is a new array on every poll, and the
  // effect must not re-run — and re-open — behind the agent's back.
  const inboxesRef = useRef(inboxes);
  useEffect(() => {
    inboxesRef.current = inboxes;
  });
  const ready = !inboxesLoading;

  useEffect(() => {
    const intent = parseReachIntent(to, tab);
    if (intent === null) {
      // Something unusable arrived in `to` — clear it rather than leave it in
      // the URL to be re-parsed on every later navigation.
      if (to !== null) handlersRef.current.clear();
      return;
    }
    if (!ready) return;

    let cancelled = false;
    const opened = (conversationId: number) => {
      if (cancelled) return;
      handlersRef.current.openConversation(conversationId, intent.channelKey);
    };
    const compose = (next: ReachSeed) => {
      if (cancelled) return;
      handlersRef.current.clear();
      setSeed(next);
    };

    void (async () => {
      try {
        const contacts = await source.api.listContacts(intent.to);
        const contact = pickContact(contacts, intent);
        if (contact === null) {
          compose({ intent, contact: null });
          return;
        }
        const conversations = await source.api.listContactConversations(contact.id);
        const conversation = pickConversation(conversations, inboxesRef.current, intent.channelKey);
        if (conversation === null) {
          compose({ intent, contact });
          return;
        }
        opened(conversation.id);
      } catch {
        compose({ intent, contact: null });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [to, tab, ready, source]);

  return { seed, clearSeed: () => setSeed(null) };
}
